import { getTranslation } from '@easy-expense/intl-client';
import { Icon } from '@easy-expense/ui-shared-components';
import { theme } from '@easy-expense/ui-theme';
import { Layout, OpenSans, Spacer } from '@easy-expense/ui-web-core';
import { MAX_TEAM_SEATS } from '@easy-expense/utils-shared';
import React from 'react';

import { ANNUAL_DISCOUNT, formatPrice } from '../../../lib/Billing/constants';
import { BillingTerm } from '../../../lib/Paddle';
import { Button } from '../../Button.components';
import { Modal } from '../../Shared/Modal.component';

const UpgradeNotice: React.FC = () => (
  <>
    <Spacer.Vertical size={8} />
    <OpenSans.Secondary size="s-16">
      {getTranslation(
        'All of your existing seats will be moved to this plan. You can purchase additional seats after the upgrade.',
      )}
    </OpenSans.Secondary>
  </>
);

const UpgradeSeatDisplay: React.FC<{ seats: number }> = ({ seats }) => (
  <Layout.Column center>
    <OpenSans.Primary size="3xl-38" weight="bold-700">
      {seats}
    </OpenSans.Primary>
    <OpenSans.Secondary size="xs-12">
      {seats === 1 ? getTranslation('seat') : getTranslation('seats')}
    </OpenSans.Secondary>
  </Layout.Column>
);

type StepperState = {
  bg: 'grayXLight' | 'brandPrimaryXLight';
  borderColor: 'grayLight' | 'brandPrimary';
  opacity: number;
  iconColor: string;
};

function getStepperState(disabled: boolean): StepperState {
  if (disabled) {
    return {
      bg: 'grayXLight',
      borderColor: 'grayLight',
      opacity: 0.5,
      iconColor: theme.colors.grayDark,
    };
  }
  return {
    bg: 'brandPrimaryXLight',
    borderColor: 'brandPrimary',
    opacity: 1,
    iconColor: theme.colors.brandPrimary,
  };
}

const SeatCounter: React.FC<{
  seats: number;
  seatLabel: string;
  minusDisabled: boolean;
  plusDisabled: boolean;
  onDecrement: () => void;
  onIncrement: () => void;
  // Optional override for the seat-count display. When provided, this is
  // rendered in place of the static number — used by callers that need an
  // editable input rather than a read-only number.
  seatDisplay?: React.ReactNode;
  seatColumnMinWidth?: number;
}> = ({
  seats,
  seatLabel,
  minusDisabled,
  plusDisabled,
  onDecrement,
  onIncrement,
  seatDisplay,
  seatColumnMinWidth = 80,
}) => {
  const minus = getStepperState(minusDisabled);
  const plus = getStepperState(plusDisabled);

  return (
    <Layout.Row align justify="center">
      <Layout.PressableColumn
        bg={minus.bg}
        border={[1, 'solid', minus.borderColor]}
        radius={100}
        center
        style={{ width: 48, height: 48, opacity: minus.opacity }}
        onClick={onDecrement}
      >
        <Icon name="remove-outline" size={24} color={minus.iconColor} />
      </Layout.PressableColumn>

      <Spacer.Horizontal size={32} />

      <Layout.Column center style={{ minWidth: seatColumnMinWidth }}>
        {seatDisplay ?? (
          <OpenSans.Primary size="3xl-38" weight="bold-700">
            {seats}
          </OpenSans.Primary>
        )}
        <OpenSans.Secondary size="xs-12">{seatLabel}</OpenSans.Secondary>
      </Layout.Column>

      <Spacer.Horizontal size={32} />

      <Layout.PressableColumn
        bg={plus.bg}
        border={[1, 'solid', plus.borderColor]}
        radius={100}
        center
        style={{ width: 48, height: 48, opacity: plus.opacity }}
        onClick={onIncrement}
      >
        <Icon name="add-outline" size={24} color={plus.iconColor} />
      </Layout.PressableColumn>
    </Layout.Row>
  );
};

export const SeatSelectionModal: React.FC<{
  showModal: boolean;
  setShowModal: (show: boolean) => void;
  planTitle: string;
  monthlyPricePerSeat: number;
  minSeats: number;
  term: BillingTerm;
  isUpgrade?: boolean;
  // When the existing subscription already covers all active users, the user is
  // purchasing additional capacity on top of what they already have. In that
  // mode the counter represents "seats to add" (starts at 1, no minimum
  // footnote) and we translate to a new total on confirm via currentAllocation.
  mode?: 'total' | 'additional';
  currentAllocation?: number;
  initialAdditionalSeats?: number;
  onConfirm: (newTotalSeats: number) => void;
}> = ({
  showModal,
  setShowModal,
  planTitle,
  monthlyPricePerSeat,
  minSeats,
  term,
  isUpgrade = false,
  mode = 'total',
  currentAllocation = 0,
  initialAdditionalSeats,
  onConfirm,
}) => {
  const isAdditional = mode === 'additional' && !isUpgrade;
  const initialCount = isAdditional ? Math.max(initialAdditionalSeats ?? 1, 1) : minSeats;
  const minCount = isAdditional ? 1 : minSeats;
  const maxCount = isAdditional
    ? Math.max(MAX_TEAM_SEATS - currentAllocation, minCount)
    : MAX_TEAM_SEATS;

  const [seats, setSeats] = React.useState<number>(initialCount);
  // Mirror seats into a separate string while the user is typing so they can
  // clear the field, type any prefix, etc., without us snapping to a clamped
  // value on every keystroke. Commit + clamp on blur / Enter.
  const [seatsInput, setSeatsInput] = React.useState<string>(String(initialCount));

  React.useEffect(() => {
    if (showModal) {
      setSeats(initialCount);
      setSeatsInput(String(initialCount));
    }
  }, [showModal, initialCount]);

  React.useEffect(() => {
    setSeatsInput(String(seats));
  }, [seats]);

  const commitSeatsInput = () => {
    const parsed = parseInt(seatsInput, 10);
    if (Number.isNaN(parsed)) {
      setSeatsInput(String(seats));
      return;
    }
    const clamped = Math.min(Math.max(parsed, minCount), maxCount);
    setSeats(clamped);
    setSeatsInput(String(clamped));
  };

  const minusDisabled = seats <= minCount;
  const plusDisabled = seats >= maxCount;
  const projectedTotal = isAdditional ? currentAllocation + seats : seats;
  const atSeatCap = projectedTotal >= MAX_TEAM_SEATS;

  let total: number;
  if (term === 'annual') {
    total = seats * monthlyPricePerSeat * 12 * (1 - ANNUAL_DISCOUNT);
  } else {
    total = seats * monthlyPricePerSeat;
  }

  const periodLabel = term === 'annual' ? getTranslation('per year') : getTranslation('per month');

  const billingFrequencyLabel =
    term === 'annual' ? getTranslation('Annual billing') : getTranslation('Monthly billing');

  const minimumReason = getTranslation('to cover your active seats');

  let modalTitle: string;
  if (isUpgrade) {
    modalTitle = `${getTranslation('Upgrade to')} ${planTitle}`;
  } else {
    modalTitle = getTranslation('Select number of seats');
  }

  let seatLabel: string;
  if (isAdditional) {
    seatLabel =
      seats === 1 ? getTranslation('additional seat') : getTranslation('additional seats');
  } else {
    seatLabel = seats === 1 ? getTranslation('seat') : getTranslation('seats');
  }

  let primaryCtaLabel: string;
  if (isUpgrade) {
    primaryCtaLabel = getTranslation('Confirm Upgrade');
  } else if (isAdditional) {
    primaryCtaLabel = getTranslation('Purchase Seats');
  } else {
    primaryCtaLabel = getTranslation('Continue to Checkout');
  }

  return (
    <Modal showModal={showModal} setShowModal={setShowModal}>
      <Layout.Column style={{ minWidth: 360, maxWidth: 420 }}>
        <OpenSans.Primary size="xl-28" weight="bold-700">
          {modalTitle}
        </OpenSans.Primary>

        <Spacer.Vertical size={8} />

        <OpenSans.Secondary size="s-16">
          {`${planTitle} — ${billingFrequencyLabel}`}
        </OpenSans.Secondary>

        {isUpgrade ? <UpgradeNotice /> : null}

        <Spacer.Vertical size={24} />

        {isUpgrade ? (
          <UpgradeSeatDisplay seats={seats} />
        ) : (
          <SeatCounter
            seats={seats}
            seatLabel={seatLabel}
            minusDisabled={minusDisabled}
            plusDisabled={plusDisabled}
            onDecrement={() => {
              if (minusDisabled) {
                return;
              }
              setSeats((current) => Math.max(current - 1, minCount));
            }}
            onIncrement={() => {
              if (plusDisabled) {
                return;
              }
              setSeats((current) => Math.min(current + 1, maxCount));
            }}
            seatColumnMinWidth={120}
            seatDisplay={
              <OpenSans.Input
                name="seats"
                value={seatsInput}
                type="number"
                inputMode="numeric"
                min={minCount}
                max={maxCount}
                weight="bold-700"
                size={38}
                title={getTranslation('Click to edit')}
                style={{
                  outline: 'none',
                  border: `1px dashed ${theme.colors.inputBorder}`,
                  borderRadius: 8,
                  background: theme.colors.grayXXLight,
                  width: 120,
                  textAlign: 'center',
                  cursor: 'text',
                  padding: '4px 8px',
                  MozAppearance: 'textfield',
                }}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                  setSeatsInput(event.target.value)
                }
                onFocus={(event: React.FocusEvent<HTMLInputElement>) => {
                  event.target.select();
                }}
                onBlur={commitSeatsInput}
                onKeyDown={(event: React.KeyboardEvent<HTMLInputElement>) => {
                  if (event.key === 'Enter') {
                    (event.target as HTMLInputElement).blur();
                  }
                }}
              />
            }
          />
        )}

        <Spacer.Vertical size={16} />

        {!isUpgrade && !isAdditional && minSeats > 1 ? (
          <OpenSans.Secondary size="xs-12" center>
            {`${getTranslation('Minimum')}: ${minSeats} ${getTranslation('seats')} (${minimumReason})`}
          </OpenSans.Secondary>
        ) : null}

        {atSeatCap ? (
          <OpenSans.Custom color="destructive" size="xs-12" center>
            {`${getTranslation('Maximum')}: ${MAX_TEAM_SEATS} ${getTranslation('seats')}. ${getTranslation(
              'Contact support@easy-expense.com for more seats.',
            )}`}
          </OpenSans.Custom>
        ) : null}

        <Spacer.Vertical size={24} />

        <Layout.Column bg="grayXXLight" radius={8} px={16} py={16}>
          <Layout.Row
            justify="space-between"
            style={{ width: '100%', columnGap: 16, rowGap: 4, flexWrap: 'wrap' }}
          >
            <OpenSans.Primary size="s-16">
              {isAdditional
                ? getTranslation('Added to your bill')
                : getTranslation('Estimated total')}
            </OpenSans.Primary>
            <OpenSans.Primary size="s-16" weight="bold-700">
              {`${formatPrice(total)} ${periodLabel}, ${getTranslation('excluding tax')}`}
            </OpenSans.Primary>
          </Layout.Row>
          {isAdditional ? null : (
            <>
              <Spacer.Vertical size={8} />
              <OpenSans.Secondary size="xs-12">
                {getTranslation(
                  'Your final price, including tax and in your local currency, is shown at checkout.',
                )}
              </OpenSans.Secondary>
            </>
          )}
        </Layout.Column>

        <Spacer.Vertical size={24} />

        <Layout.Row>
          <Button.Secondary
            content={getTranslation('Cancel')}
            onClick={() => setShowModal(false)}
          />
          <Spacer.Horizontal size={12} />
          <Button.Primary
            content={primaryCtaLabel}
            onClick={() => {
              const newTotal = isAdditional ? currentAllocation + seats : seats;
              onConfirm(newTotal);
              setShowModal(false);
            }}
          />
        </Layout.Row>
      </Layout.Column>
    </Modal>
  );
};
